"use client"

import { useState, useEffect } from "react"
import { collection, query, where, getDocs, doc, updateDoc, orderBy, getDoc } from "firebase/firestore"
import { db } from "../../firebaseconfig"
import Header from "./Header"
import PostList from "./PostList"
import PostModal from "./PostModal"
import AreaBudgetVisualization from "./AreaBudgetVisualization"
import { fetchUsers } from "../../utils/userUtils"
import { addOrUpdatePost, deletePost } from "../../utils/postUtils"
import "../../App.css"
import "../../css/ControlPage.css"
import "../../css/nav.css"
import "../../css/OfficePage.css"

const updateUsedArea = async (area, posts) => {
  const areaInvestment = posts
    .filter((post) => post.area === area)
    .reduce((sum, post) => sum + (Number.parseFloat(post.costo) || 0), 0)

  const totalInvestment = posts.reduce((sum, post) => sum + (Number.parseFloat(post.costo) || 0), 0)

  const areaDocRef = doc(db, "budgets", area)
  await updateDoc(areaDocRef, {
    usedarea: areaInvestment,
    used: totalInvestment,
  })
}

const OfficePage = ({
  username,
  userArea,
  onLogout,
  onChangePassword,
  setIsChangePasswordModalOpen,
  darkMode,
  toggleDarkMode,
}) => {
  const [posts, setPosts] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [editPostId, setEditPostId] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [sortBy, setSortBy] = useState("fecha")
  const [sortOrder, setSortOrder] = useState("desc")
  const [users, setUsers] = useState({})
  const [relatedAreas, setRelatedAreas] = useState([])

  useEffect(() => {
    fetchUsers().then(setUsers)
  }, [])

  useEffect(() => {
    if (Object.keys(users).length > 0) {
      loadRelatedAreas()
    }
  }, [users]) // Removed userArea from dependencies

  useEffect(() => {
    if (relatedAreas.length > 0) {
      loadPosts()
    }
  }, [relatedAreas, sortBy, sortOrder])

  const loadRelatedAreas = async () => {
    try {
      const areaQuery = query(collection(db, "areacontroller"), where("from", "==", userArea))
      const areaDocs = await getDocs(areaQuery)
      const areas = areaDocs.docs.map((doc) => doc.data().name)
      setRelatedAreas([userArea, ...areas])
    } catch (error) {
      console.error("Error cargando áreas relacionadas:", error)
    }
  }

  const loadPosts = async () => {
    try {
      let allPosts = []
      for (const area of relatedAreas) {
        const areaPosts = await getDocs(query(collection(db, area), orderBy(sortBy, sortOrder)))
        allPosts = [
          ...allPosts,
          ...areaPosts.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            area,
          })),
        ]
      }
      setPosts(allPosts)
    } catch (error) {
      console.error("Error cargando publicaciones:", error)
    }
  }

  const handleSubmit = async (formData) => {
    try {
      const updatedPost = await addOrUpdatePost(userArea, formData, editPostId, formData.area)

      setPosts((prevPosts) => {
        let newPosts
        if (editPostId) {
          newPosts = prevPosts.map((post) => (post.id === editPostId ? updatedPost : post))
        } else {
          newPosts = [...prevPosts, updatedPost]
        }
        return newPosts
      })

      // Update the used budget in Firestore
      const areaDocRef = doc(db, "budgets", userArea)
      const areaDoc = await getDoc(areaDocRef)
      if (areaDoc.exists()) {
        const currentUsed = areaDoc.data().used || 0
        let newUsed

        if (editPostId) {
          const oldPost = posts.find((post) => post.id === editPostId)
          const oldCost = Number.parseFloat(oldPost.costo) || 0
          const newCost = Number.parseFloat(formData.costo) || 0
          newUsed = currentUsed - oldCost + newCost
        } else {
          newUsed = currentUsed + (Number.parseFloat(formData.costo) || 0)
        }

        await updateDoc(areaDocRef, { used: newUsed })
      }

      setShowModal(false)
      setEditPostId(null)
    } catch (error) {
      console.error("Error updating post and budget:", error)
    }
  }

  const handleEdit = (postId) => {
    setEditPostId(postId)
    setShowModal(true)
  }

  const handleDelete = async (postId) => {
    try {
      const postToDelete = posts.find((post) => post.id === postId)
      if (postToDelete) {
        await deletePost(postToDelete.area, postId)

        // Update the used budget in Firestore
        const areaDocRef = doc(db, "budgets", userArea)
        const areaDoc = await getDoc(areaDocRef)
        if (areaDoc.exists()) {
          const currentUsed = areaDoc.data().used || 0
          const newUsed = currentUsed - (Number.parseFloat(postToDelete.costo) || 0)
          await updateDoc(areaDocRef, { used: newUsed })
        }

        setPosts((prevPosts) => prevPosts.filter((post) => post.id !== postId))
      }
    } catch (error) {
      console.error("Error deleting post:", error)
    }
  }

  return (
    <div className={`container ${darkMode ? "dark-mode" : ""}`}>
      <Header
        userArea={userArea}
        onAddPost={() => {
          setEditPostId(null)
          setShowModal(true)
        }}
        onLogout={onLogout}
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
        setIsChangePasswordModalOpen={setIsChangePasswordModalOpen}
      />
      <main>
        <PostList
          posts={posts}
          users={users}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          sortBy={sortBy}
          sortOrder={sortOrder}
          setSortBy={setSortBy}
          setSortOrder={setSortOrder}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
        <AreaBudgetVisualization selectedArea={userArea} />
      </main>
      {showModal && (
        <PostModal
          onSubmit={handleSubmit}
          onClose={() => {
            setShowModal(false)
            setEditPostId(null)
          }}
          initialData={editPostId ? posts.find((post) => post.id === editPostId) : null}
          username={username}
          userArea={userArea}
        />
      )}
    </div>
  )
}

export default OfficePage

